import React, { useMemo } from 'react';
import PropTypes from 'prop-types';

/**
 * CourseStatisticsTab - Statistiken Tab in der CourseView
 * 
 * Zeigt aggregierte Statistiken des Kurses:
 * - Übersicht (Quizze, Teilnehmer, Durchschnitt)
 * - Performance pro Vorlage
 * - Schwierigste Fragen (TODO)
 */
export default function CourseStatisticsTab({
  templates,
  instances
}) {
  // Calculate stats
  const stats = useMemo(() => {
    const totalQuizzes = instances.length;
    const totalParticipants = instances.reduce((sum, i) => sum + (i.participants?.length || 0), 0);
    
    const scoresWithValues = instances
      .filter(i => i.averageScore !== undefined && i.averageScore !== null);
    const averageScore = scoresWithValues.length > 0
      ? scoresWithValues.reduce((sum, i) => sum + i.averageScore, 0) / scoresWithValues.length
      : 0;

    // Calculate average duration
    const durations = instances
      .filter(i => i.startedAt && i.endedAt)
      .map(i => (new Date(i.endedAt) - new Date(i.startedAt)) / 1000 / 60);
    const avgDuration = durations.length > 0
      ? durations.reduce((sum, d) => sum + d, 0) / durations.length
      : 0;

    return {
      totalQuizzes,
      totalParticipants,
      averageScore: Math.round(averageScore * 10) / 10,
      avgDuration: Math.round(avgDuration * 10) / 10
    };
  }, [instances]);

  // Calculate per-template stats
  const templateStats = useMemo(() => {
    return templates.map(template => {
      const templateInstances = instances.filter(i => i.templateId === template.id);
      const totalSessions = templateInstances.length;
      const totalParticipants = templateInstances.reduce((sum, i) => sum + (i.participants?.length || 0), 0);
      
      const scoresWithValues = templateInstances
        .filter(i => i.averageScore !== undefined && i.averageScore !== null);
      const averageScore = scoresWithValues.length > 0
        ? scoresWithValues.reduce((sum, i) => sum + i.averageScore, 0) / scoresWithValues.length
        : null;

      return {
        id: template.id,
        name: template.name,
        sessions: totalSessions,
        participants: totalParticipants,
        averageScore: averageScore !== null ? Math.round(averageScore) : null
      };
    }).sort((a, b) => b.sessions - a.sessions);
  }, [templates, instances]);

  // Format duration
  const formatDuration = (minutes) => {
    if (minutes < 1) return '< 1 Min';
    if (minutes < 60) return `${Math.round(minutes)} Min`;
    const hours = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);
    return `${hours}:${mins.toString().padStart(2, '0')}`;
  };

  return (
    <div className="space-y-6">
      {/* Overview Stats */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Übersicht
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          <div className="text-center">
            <div className="text-3xl font-bold text-primary-600 dark:text-primary-400">
              {stats.totalQuizzes}
            </div>
            <div className="text-sm text-gray-500 dark:text-gray-400">Quizze</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-secondary-600 dark:text-secondary-400">
              {stats.totalParticipants}
            </div>
            <div className="text-sm text-gray-500 dark:text-gray-400">Teilnehmer</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-success-600 dark:text-success-400">
              {stats.averageScore}%
            </div>
            <div className="text-sm text-gray-500 dark:text-gray-400">Ø Score</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-warning-600 dark:text-warning-400">
              {formatDuration(stats.avgDuration)}
            </div>
            <div className="text-sm text-gray-500 dark:text-gray-400">Ø Dauer</div>
          </div>
        </div>
      </div>

      {/* Performance per Template */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            Performance pro Vorlage
          </h3>
          <button 
            onClick={() => alert('CSV Export wird noch implementiert')}
            className="text-sm text-primary-600 dark:text-primary-400 hover:underline flex items-center gap-1"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Als CSV exportieren
          </button>
        </div>
        
        {templateStats.length === 0 ? (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">
            Noch keine Daten vorhanden
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-700">
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-500 dark:text-gray-400">
                    Vorlage
                  </th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-gray-500 dark:text-gray-400">
                    Sessions
                  </th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-gray-500 dark:text-gray-400">
                    Teilnehmer
                  </th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-gray-500 dark:text-gray-400">
                    Ø Score
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {templateStats.map((template) => (
                  <tr key={template.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                    <td className="py-3 px-4">
                      <span className="font-medium text-gray-900 dark:text-white">
                        {template.name}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-right text-gray-600 dark:text-gray-300">
                      {template.sessions}
                    </td>
                    <td className="py-3 px-4 text-right text-gray-600 dark:text-gray-300">
                      {template.participants}
                    </td>
                    <td className="py-3 px-4 text-right">
                      {template.averageScore !== null ? (
                        <span className={`font-medium ${
                          template.averageScore >= 70 
                            ? 'text-success-600 dark:text-success-400' 
                            : template.averageScore >= 50 
                            ? 'text-warning-600 dark:text-warning-400'
                            : 'text-danger-600 dark:text-danger-400'
                        }`}>
                          {template.averageScore}%
                        </span>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Placeholder for Difficult Questions */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Schwierigste Fragen
        </h3>
        <div className="text-center py-8 text-gray-500 dark:text-gray-400">
          <svg className="w-16 h-16 mx-auto mb-2 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
          <p>Diese Funktion wird in einer zukünftigen Version verfügbar sein.</p>
        </div>
      </div>
    </div>
  );
}

CourseStatisticsTab.propTypes = {
  templates: PropTypes.array.isRequired,
  instances: PropTypes.array.isRequired
};
